import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
  "https://us-central1-sistema-bks.cloudfunctions.net";

export default function EcommerceIntegration({ user, config, onUpdate, checkIntegrationSlot, organizationId }) {
  const { t } = useTranslation('bots');
  const [showShopifyForm, setShowShopifyForm] = useState(false);
  const [reemplazarApiSecret, setReemplazarApiSecret] = useState(false);
  const [showWooForm, setShowWooForm] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [ecommerceStatus, setEcommerceStatus] = useState(null);
  const [revokeModal, setRevokeModal] = useState(null); // { url } | null
  const [wooAutoConnect, setWooAutoConnect] = useState(false);
  const [wcAuthPending, setWcAuthPending] = useState(false);
  const [showWooManual, setShowWooManual] = useState(false);

  const [shopifyConfig, setShopifyConfig] = useState({
    shopDomain: '',
    clientId: '',
    clientSecret: '',
  });

  const [wooConfig, setWooConfig] = useState({
    baseUrl: '',
    consumerKey: '',
    consumerSecret: '',
  });

  // Subscribe to the runtime status doc. Lives separately from config so
  // clients cannot silence error alerts by editing their own config.
  useEffect(() => {
    if (!user?.uid) return undefined;
    const basePath = organizationId
      ? `organizations/${organizationId}`
      : `clientes/${user.uid}`;
    const ref = doc(db, `${basePath}/integrations/status`);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        setEcommerceStatus(snap.exists() ? snap.data()?.ecommerce || null : null);
      },
      (err) => {
        console.warn('[EcommerceIntegration] status subscription error:', err.message);
      }
    );
    return unsubscribe;
  }, [user?.uid, organizationId]);

  // Detect return from WC auto-auth via sessionStorage.
  // While pending, subscribe to config doc to detect when credentials land.
  useEffect(() => {
    const pending = sessionStorage.getItem('wc_auth_pending');
    if (!pending || !user?.uid) return;
    setWcAuthPending(true);

    const basePath = organizationId
      ? `organizations/${organizationId}`
      : `clientes/${user.uid}`;
    const ref = doc(db, `${basePath}/integrations/config`);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists() && snap.data()?.ecommerce?.provider === 'woocommerce') {
        sessionStorage.removeItem('wc_auth_pending');
        setWcAuthPending(false);
        onUpdate({ ecommerce: snap.data().ecommerce });
      }
    });
    const timeout = setTimeout(() => {
      unsub();
      sessionStorage.removeItem('wc_auth_pending');
      setWcAuthPending(false);
      setShowWooForm(true);
      setShowWooManual(true);
    }, 30000);
    return () => { unsub(); clearTimeout(timeout); };
  }, [user?.uid, organizationId]);

  const handleWcAutoConnect = async () => {
    if (checkIntegrationSlot) {
      const slotCheck = checkIntegrationSlot('ecommerce');
      if (!slotCheck.allowed) {
        alert(slotCheck.reason);
        return;
      }
    }

    if (!wooConfig.baseUrl) {
      alert(t('integrations.ecommerce.completeAllFields'));
      return;
    }

    try {
      setWooAutoConnect(true);
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/wcAuthStart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          storeUrl: wooConfig.baseUrl,
          organizationId,
        }),
      });

      const data = await res.json();
      if (data.authUrl) {
        sessionStorage.setItem('wc_auth_pending', '1');
        window.location.href = data.authUrl;
      } else {
        alert(data.error || t('integrations.ecommerce.saveError'));
        setWooAutoConnect(false);
      }
    } catch (error) {
      console.error('Error starting WC auto-auth:', error);
      alert(t('integrations.ecommerce.saveError'));
      setWooAutoConnect(false);
    }
  };

  const handleSaveShopify = async () => {
    // Check Starter integration slot exclusivity
    if (checkIntegrationSlot) {
      const slotCheck = checkIntegrationSlot('ecommerce');
      if (!slotCheck.allowed) {
        alert(slotCheck.reason);
        return;
      }
    }

    if (!shopifyConfig.shopDomain || !shopifyConfig.clientId || !shopifyConfig.clientSecret) {
      alert(t('integrations.ecommerce.completeAllFields'));
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/saveShopifyConfig`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          config: shopifyConfig,
          organizationId,
        }),
      });

      const data = await res.json();
      if (data.success) {
        onUpdate({
          ecommerce: {
            provider: 'shopify',
            shopDomain: shopifyConfig.shopDomain,
            connectedAt: new Date().toISOString(),
          },
        });
        setShowShopifyForm(false);
      } else {
        // ANTES ACÁ NO HABÍA NADA (12-ago-2026). Si la respuesta no traía
        // `success`, el formulario se quedaba quieto: ni mensaje, ni error, ni
        // pista. El cliente apretaba Guardar y no pasaba nada.
        //
        // El backend ahora manda un 400 con un texto accionable cuando el fallo
        // es del comerciante —credenciales mal, app desinstalada, tienda
        // inexistente— y un 500 genérico para todo lo demás. Se muestra el que
        // venga; si no viene ninguno, el genérico de siempre.
        alert(data.error || t('integrations.ecommerce.saveError'));
      }
    } catch (error) {
      // Sólo fallos de red: el backend ya respondió arriba.
      console.error('Error saving Shopify:', error);
      alert(t('integrations.ecommerce.saveError'));
    }
  };

  const handleSaveWoo = async () => {
    // Check Starter integration slot exclusivity
    if (checkIntegrationSlot) {
      const slotCheck = checkIntegrationSlot('ecommerce');
      if (!slotCheck.allowed) {
        alert(slotCheck.reason);
        return;
      }
    }

    if (!wooConfig.baseUrl || !wooConfig.consumerKey || !wooConfig.consumerSecret) {
      alert(t('integrations.ecommerce.completeAllFields'));
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/saveWooConfig`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          config: wooConfig,
          organizationId,
        }),
      });

      const data = await res.json();
      if (data.success) {
        onUpdate({
          ecommerce: {
            provider: 'woocommerce',
            baseUrl: wooConfig.baseUrl,
            connectedAt: new Date().toISOString(),
          },
        });
        setShowWooForm(false);
      }
    } catch (error) {
      console.error('Error saving WooCommerce:', error);
      alert(t('integrations.ecommerce.saveError'));
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('integrations.ecommerce.confirmDisconnect'))) {
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/disconnectEcommerce`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ uid: user.uid, organizationId }),
      });
      const data = await res.json().catch(() => ({}));

      onUpdate({ ecommerce: { provider: null } });

      // WC has no programmatic key revocation — remind the merchant to
      // delete the keys in wp-admin. Backend signals this via showRevokeModal.
      if (data?.showRevokeModal && data?.revokeUrl) {
        setRevokeModal({ url: data.revokeUrl });
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/testEcommerceConnection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ uid: user.uid, organizationId }),
      });

      const data = await res.json();
      setTestResult(data);
    } catch (error) {
      setTestResult({ success: false, error: t('integrations.connectionError') });
    } finally {
      setTesting(false);
    }
  };

  const isConnected = config?.ecommerce?.provider;
  // Sólo el NOMBRE del secreto llega al frontend, nunca el valor — por eso alcanza
  // con preguntar si existe para decidir entre "Configurada" y pedirla.
  const yaTieneApiSecret = !!config?.ecommerce?.apiSecretSecretName;
  const provider = config?.ecommerce?.provider;
  const metadata = config?.ecommerce?.metadata || null;
  const isErrorToken = ecommerceStatus?.state === 'error_token';
  const errorReason = ecommerceStatus?.errorReason || null;

  // Format relative "last checked" — pure function, no date-fns dep required.
  const formatRelative = (iso) => {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return null;
    const diffMin = Math.round((Date.now() - then) / 60000);
    if (diffMin < 1) return t('ecommerce.metadata.justNow');
    if (diffMin < 60) return t('ecommerce.metadata.minutesAgo', { count: diffMin });
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return t('ecommerce.metadata.hoursAgo', { count: diffHr });
    const diffDay = Math.round(diffHr / 24);
    return t('ecommerce.metadata.daysAgo', { count: diffDay });
  };

  const handleReconnect = () => {
    if (provider === 'shopify') setShowShopifyForm(true);
    else if (provider === 'woocommerce') setShowWooForm(true);
  };

  return (
    <div className="integration-card">
      <div className="integration-header">
        <div className="integration-icon">🛒</div>
        <div>
          <h3>{t('integrations.ecommerce.title')}</h3>
          <p className="integration-description">
            {t('integrations.ecommerce.description')}
          </p>
        </div>
      </div>

      {wcAuthPending ? (
        <div className="wc-auth-pending">
          <div className="spinner" />
          <p>{t('integrations.ecommerce.wcAuthPending')}</p>
        </div>
      ) : !isConnected ? (
        <div className="connection-options">
          <p className="help-text">{t('integrations.ecommerce.selectPlatform')}</p>

          <div className="provider-buttons">
            <button
              onClick={() => {
                setShowShopifyForm(!showShopifyForm);
                setShowWooForm(false);
              }}
              className="provider-btn shopify"
            >
              <span className="provider-icon">🛍️</span>
              {t('integrations.ecommerce.configureShopify')}
            </button>
            <button
              onClick={() => {
                setShowWooForm(!showWooForm);
                setShowShopifyForm(false);
              }}
              className="provider-btn woocommerce"
            >
              <span className="provider-icon">🔌</span>
              {t('integrations.ecommerce.configureWoo')}
            </button>
          </div>

          {showShopifyForm && (
            <div className="config-form">
              <h4>{t('integrations.ecommerce.shopifyConfig')}</h4>
              <div className="form-group">
                <label>Shop Domain *</label>
                <input
                  type="text"
                  value={shopifyConfig.shopDomain}
                  onChange={e =>
                    setShopifyConfig({ ...shopifyConfig, shopDomain: e.target.value })
                  }
                  placeholder={t('integrations.ecommerce.shopDomainPlaceholder')}
                />
                <small>{t('integrations.ecommerce.shopDomainHelp')}</small>
              </div>
              {/*
                MODELO DE CREDENCIALES (11-ago-2026): se pide Client ID + Client Secret,
                NO un access token.

                Shopify eliminó los tokens permanentes — el access token vive 24 h y hasta
                hoy había que sacarlo con un curl y pegarlo acá. El 11-ago venció en medio
                de una conversación real y el bot dejó de vender. Ahora PUX hace el
                intercambio y renueva solo.
              */}
              <div className="form-group">
                <label>ID de cliente (Client ID) *</label>
                <input
                  type="text"
                  value={shopifyConfig.clientId}
                  onChange={e =>
                    setShopifyConfig({ ...shopifyConfig, clientId: e.target.value })
                  }
                  placeholder="1a2b3c4d5e6f..."
                  autoComplete="off"
                />
                <small>
                  En el <strong>Dev Dashboard</strong> de Shopify: tu app →{' '}
                  <strong>Configuración → Credenciales</strong>, campo{' '}
                  <strong>«ID de cliente»</strong>. La app tiene que estar creada en{' '}
                  <strong>tu organización</strong> e instalada en <strong>tu tienda</strong>.
                </small>
              </div>
              {/*
                Client Secret. Hace DOBLE trabajo, y por eso el formulario tiene TRES
                campos y no cuatro: obtiene el access token cada 24 h Y es el secreto
                con el que Shopify firma los webhooks — verificado en su documentación
                ("generated using your app's client secret"). Antes se pedía aparte
                como «Clave secreta de la API»: es el mismo valor.

                El valor NUNCA vuelve del servidor: si ya hay uno guardado se muestra
                el estado y se ofrece reemplazarlo, nunca el valor.
              */}
              <div className="form-group">
                <label>Secreto (Client Secret) *</label>
                {yaTieneApiSecret && !reemplazarApiSecret ? (
                  <div className="secret-configured">
                    <span>🔒 Configurada</span>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => setReemplazarApiSecret(true)}
                    >
                      Reemplazar
                    </button>
                  </div>
                ) : (
                  <input
                    type="password"
                    value={shopifyConfig.clientSecret}
                    onChange={e =>
                      setShopifyConfig({ ...shopifyConfig, clientSecret: e.target.value })
                    }
                    placeholder="••••••••••••••••"
                    autoComplete="new-password"
                  />
                )}
                <small>
                  En el mismo lugar que el ID de cliente, campo <strong>«Secreto»</strong>.
                  PUX lo usa para DOS cosas: renovar el acceso a tu tienda cada 24 horas,
                  y verificar que los avisos de pedidos vengan de verdad de Shopify.
                </small>
              </div>
              <button onClick={handleSaveShopify} className="btn-save">
                {t('integrations.ecommerce.saveShopify')}
              </button>
            </div>
          )}

          {showWooForm && (
            <div className="config-form">
              <h4>{t('integrations.ecommerce.wooConfig')}</h4>
              <div className="form-group">
                <label>URL Base *</label>
                <input
                  type="url"
                  value={wooConfig.baseUrl}
                  onChange={e => setWooConfig({ ...wooConfig, baseUrl: e.target.value })}
                  placeholder={t('integrations.ecommerce.baseUrlPlaceholder')}
                />
                <small>{t('integrations.ecommerce.baseUrlHelp')}</small>
              </div>

              <button
                onClick={handleWcAutoConnect}
                disabled={wooAutoConnect}
                className="btn-save"
              >
                {wooAutoConnect
                  ? t('integrations.ecommerce.wcAuthRedirecting')
                  : t('integrations.ecommerce.wcAutoConnect')}
              </button>

              <div className="woo-manual-toggle">
                <button
                  className="link-btn"
                  onClick={() => setShowWooManual(!showWooManual)}
                >
                  {t('integrations.ecommerce.orManualKeys')}
                </button>
              </div>

              {showWooManual && (
                <>
                  <div className="form-group">
                    <label>Consumer Key *</label>
                    <input
                      type="text"
                      value={wooConfig.consumerKey}
                      onChange={e =>
                        setWooConfig({ ...wooConfig, consumerKey: e.target.value })
                      }
                      placeholder="ck_..."
                    />
                  </div>
                  <div className="form-group">
                    <label>Consumer Secret *</label>
                    <input
                      type="password"
                      value={wooConfig.consumerSecret}
                      onChange={e =>
                        setWooConfig({ ...wooConfig, consumerSecret: e.target.value })
                      }
                      placeholder="cs_..."
                    />
                    <small>
                      {t('integrations.ecommerce.restApiCredentials')} (
                      <a
                        href="https://woocommerce.com/document/woocommerce-rest-api/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('integrations.ecommerce.howToGetCredentials')}
                      </a>
                      )
                    </small>
                  </div>
                  <button onClick={handleSaveWoo} className="btn-save">
                    {t('integrations.ecommerce.saveWoo')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ) : isErrorToken ? (
        <div className="error-banner" role="alert">
          <div className="error-banner-title">
            ⚠️ {t(`ecommerce.error.${errorReason || 'generic'}`, {
              defaultValue: t('ecommerce.error.generic'),
            })}
          </div>
          <div className="error-banner-body">
            {t('ecommerce.reconnect.message')}
          </div>
          <div className="actions">
            <button onClick={handleReconnect} className="btn-save">
              {t('ecommerce.reconnect.cta')}
            </button>
            <button onClick={handleDisconnect} className="btn-disconnect">
              {t('integrations.disconnect')}
            </button>
          </div>
        </div>
      ) : (
        <div className="connected-section">
          <div className="status-badge connected">
            {t('integrations.connected')} -{' '}
            {provider === 'shopify' ? 'Shopify' : 'WooCommerce'}
          </div>

          {metadata?.storeName && (
            <div className="metadata-block">
              <div className="metadata-title">{metadata.storeName}</div>
              {metadata.storeUrl && (
                <div className="metadata-row">
                  <code>{metadata.storeUrl}</code>
                </div>
              )}
              {metadata.platformVersion && (
                <div className="metadata-row">
                  {t('ecommerce.metadata.version')}: {metadata.platformVersion}
                </div>
              )}
              {metadata.lastCheckedAt && (
                <div className="metadata-row subtle">
                  {t('ecommerce.metadata.lastChecked')}:{' '}
                  {formatRelative(metadata.lastCheckedAt)}
                </div>
              )}
            </div>
          )}

          {!metadata && provider === 'shopify' && config.ecommerce.shopDomain && (
            <p className="connection-meta">
              {t('integrations.ecommerce.store')}: <code>{config.ecommerce.shopDomain}</code>
            </p>
          )}

          {!metadata && provider === 'woocommerce' && config.ecommerce.baseUrl && (
            <p className="connection-meta">
              {t('integrations.ecommerce.site')}: <code>{config.ecommerce.baseUrl}</code>
            </p>
          )}

          <div className="actions">
            <button
              onClick={handleTest}
              disabled={testing}
              className="btn-test"
            >
              {testing ? t('integrations.testing') : t('integrations.ecommerce.testCatalog')}
            </button>
            <button onClick={handleDisconnect} className="btn-disconnect">
              {t('integrations.disconnect')}
            </button>
          </div>

          {testResult && (
            <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
              {testResult.success ? (
                <>
                  {t('integrations.connectionSuccess')}
                  {testResult.productCount && (
                    <span> - {t('integrations.ecommerce.productsFound', { count: testResult.productCount })}</span>
                  )}
                </>
              ) : (
                `${t('integrations.connectionFailed')}: ${testResult.error}`
              )}
            </div>
          )}
        </div>
      )}

      {revokeModal && (
        <div
          className="revoke-modal-backdrop"
          onClick={() => setRevokeModal(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="revoke-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('ecommerce.revoke.title')}</h3>
            <p>{t('ecommerce.revoke.body')}</p>
            <div className="actions">
              <a
                href={revokeModal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-save"
                onClick={() => setRevokeModal(null)}
              >
                {t('ecommerce.revoke.openWp')}
              </a>
              <button onClick={() => setRevokeModal(null)} className="btn-disconnect">
                {t('ecommerce.revoke.later')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .integration-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1rem;
        }

        .integration-header {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .integration-icon {
          font-size: 2.5rem;
        }

        .integration-card h3 {
          margin: 0 0 0.25rem 0;
          color: #2d3748;
          font-size: 1.25rem;
        }

        .integration-description {
          color: #718096;
          font-size: 0.875rem;
          margin: 0;
        }

        .connection-options {
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .help-text {
          color: #4a5568;
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }

        .provider-buttons {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .provider-btn {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.25rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 500;
          transition: all 0.2s;
        }

        .provider-btn:hover {
          border-color: #667eea;
          background: #f7fafc;
        }

        .provider-icon {
          font-size: 1.25rem;
        }

        .config-form {
          background: #f7fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          margin: 1rem 0;
        }

        .config-form h4 {
          margin: 0 0 1rem 0;
          color: #2d3748;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          display: block;
          font-size: 0.875rem;
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 0.25rem;
        }

        .form-group input {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .form-group input:focus {
          outline: none;
          border-color: #667eea;
        }

        .form-group small {
          display: block;
          font-size: 0.75rem;
          color: #718096;
          margin-top: 0.25rem;
        }

        .form-group small a {
          color: #667eea;
          text-decoration: none;
        }

        .form-group small a:hover {
          text-decoration: underline;
        }

        .btn-save {
          width: 100%;
          padding: 0.75rem;
          background: #667eea;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          margin-top: 0.5rem;
        }

        .btn-save:hover {
          background: #5568d3;
        }

        .info-box {
          background: #fff5e5;
          border: 1px solid #ffd580;
          border-radius: 6px;
          padding: 1rem;
          color: #8b5a00;
          font-size: 0.875rem;
        }

        .connected-section {
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .status-badge {
          display: inline-block;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }

        .status-badge.connected {
          background: #c6f6d5;
          color: #22543d;
        }

        .connection-meta {
          font-size: 0.875rem;
          color: #718096;
          margin: 0.5rem 0;
        }

        .connection-meta code {
          background: #f7fafc;
          padding: 0.125rem 0.375rem;
          border-radius: 3px;
          font-family: monospace;
          font-size: 0.85em;
        }

        .actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 1.5rem;
        }

        .btn-test,
        .btn-disconnect {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-test {
          background: #667eea;
          color: white;
        }

        .btn-test:hover:not(:disabled) {
          background: #5568d3;
        }

        .btn-test:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-disconnect {
          background: white;
          color: #e53e3e;
          border: 1px solid #fc8181;
        }

        .btn-disconnect:hover {
          background: #fff5f5;
        }

        .test-result {
          margin-top: 1rem;
          padding: 0.75rem;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .test-result.success {
          background: #f0fff4;
          color: #22543d;
          border: 1px solid #9ae6b4;
        }

        .test-result.error {
          background: #fff5f5;
          color: #742a2a;
          border: 1px solid #fc8181;
        }

        .metadata-block {
          padding: 0.75rem 1rem;
          background: #f7fafc;
          border-radius: 6px;
          margin: 0.75rem 0;
        }

        .metadata-title {
          font-size: 1rem;
          font-weight: 600;
          color: #2d3748;
        }

        .metadata-row {
          font-size: 0.85rem;
          color: #4a5568;
          margin-top: 0.25rem;
        }

        .metadata-row.subtle {
          color: #718096;
          font-size: 0.75rem;
        }

        .metadata-row code {
          background: #ffffff;
          padding: 0.1rem 0.375rem;
          border-radius: 3px;
          font-family: monospace;
          font-size: 0.85em;
          border: 1px solid #e2e8f0;
        }

        .error-banner {
          background: #fff5f5;
          border: 1px solid #feb2b2;
          border-left: 4px solid #e53e3e;
          border-radius: 8px;
          padding: 1.25rem;
          margin-top: 1rem;
        }

        .error-banner-title {
          font-size: 1rem;
          font-weight: 600;
          color: #742a2a;
          margin-bottom: 0.5rem;
        }

        .error-banner-body {
          font-size: 0.875rem;
          color: #742a2a;
          margin-bottom: 1rem;
          line-height: 1.5;
        }

        .revoke-modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .revoke-modal {
          background: white;
          border-radius: 10px;
          padding: 2rem;
          max-width: 480px;
          width: 90%;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
        }

        .revoke-modal h3 {
          margin: 0 0 0.75rem 0;
          color: #2d3748;
        }

        .revoke-modal p {
          color: #4a5568;
          line-height: 1.55;
          margin-bottom: 1.5rem;
        }

        .revoke-modal .btn-save {
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin: 0;
          width: auto;
          padding: 0.75rem 1.25rem;
          margin-top: 0;
        }

        .wc-auth-pending {
          text-align: center;
          padding: 2rem 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .wc-auth-pending p {
          color: #4a5568;
          margin-top: 1rem;
        }

        .spinner {
          width: 36px;
          height: 36px;
          border: 4px solid #e2e8f0;
          border-top-color: #667eea;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .woo-manual-toggle {
          text-align: center;
          margin: 1rem 0 0.5rem;
        }

        .link-btn {
          background: none;
          border: none;
          color: #667eea;
          cursor: pointer;
          font-size: 0.85rem;
          text-decoration: underline;
          padding: 0;
        }

        .link-btn:hover {
          color: #5568d3;
        }
      `}</style>
    </div>
  );
}
